(() => {
  const ROOT_SELECTOR = '#section-quick-product-v2[data-ebike-version-gallery-enabled="true"]';
  const instances = new Map();

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function colorSlug(value) {
    return normalize(value)
      .replace(/\s*&\s*/g, '-')
      .replace(/[\s/]+/g, '-')
      .replace(/-+/g, '-');
  }

  function removeGalleryColorClasses(element) {
    Array.from(element.classList).forEach((className) => {
      if (className.startsWith('alt-')) element.classList.remove(className);
    });
  }

  function selfOrDescendant(element, selector) {
    if (!element) return null;
    return element.matches(selector) ? element : element.querySelector(selector);
  }

  class EbikeVersionGallery {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.ProductForm');
      this.generation = 0;
      this.syncFrame = null;
      this.surfaceFrames = new Map();
      this.swipers = new Set();
      this.swiperElements = new Map();
      this.destroyed = false;
      this.handleChange = this.handleChange.bind(this);
      this.handleLoad = this.handleLoad.bind(this);

      if (!this.form || !root.querySelector('[data-ebike-version-gallery-group]')) return;

      this.form.addEventListener('change', this.handleChange);
      this.initialVariantId = this.selectedVariantId();
      this.alignInitialFamilyToVariant();
      this.scheduleSync();
      if (document.readyState === 'complete') {
        this.handleLoad();
      } else {
        window.addEventListener('load', this.handleLoad, { once: true });
      }
      root.dataset.ebikeVersionGalleryReady = 'true';
    }

    selectedColor() {
      return this.form.querySelector(
        'input[type="radio"][data-variant="Color"]:checked, input[type="radio"][data-variant="Colour"]:checked, input[name="option-0"]:checked'
      )?.value || '';
    }

    selectedFamily() {
      const inputs = Array.from(this.form.querySelectorAll(
        '.variant-option.version input[type="radio"]:checked, input[type="radio"][data-variant="Helmet Type"]:checked'
      ));
      const input = inputs.find((candidate) => candidate.closest('.extra-bundle')) || inputs[0];
      const value = normalize(input?.dataset.value || input?.value);
      return value.includes('standard') || value.includes('non-mips') ? 'standard' : 'mips';
    }

    selectedVariantId() {
      const select = this.form.querySelector('select[name="id"]');
      if (select?.value) return String(select.value);
      const input = this.form.querySelector('input[name="id"]');
      return input?.value ? String(input.value) : '';
    }

    alignInitialFamilyToVariant() {
      if (!this.initialVariantId) return;

      const variantGroup = Array.from(this.root.querySelectorAll('[data-ebike-version-gallery-group]')).find((group) => (
        String(group.dataset.ebikeGalleryVariantId || '') === this.initialVariantId
      ));
      const family = normalize(variantGroup?.dataset.ebikeGalleryFamily);
      if (!family) return;

      const versionInputs = Array.from(this.form.querySelectorAll(
        '.variant-option.version input[type="radio"], input[type="radio"][data-variant="Helmet Type"]'
      ));
      const target = versionInputs.find((input) => {
        const value = normalize(input.dataset.value || input.value);
        return family === 'standard'
          ? value.includes('standard') || value.includes('non-mips')
          : value === 'mips';
      });
      if (target) target.checked = true;
    }

    handleLoad() {
      if (this.destroyed || this.selectedVariantId() !== this.initialVariantId) return;
      this.alignInitialFamilyToVariant();
      this.scheduleSync();
    }

    handleChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;
      if (!event.target.matches('[data-variant="Color"], [data-variant="Colour"], [data-variant="Helmet Type"]')) return;

      this.scheduleSync();
    }

    isCurrentGeneration(generation) {
      return !this.destroyed && generation === this.generation;
    }

    cancelPendingFrames() {
      if (this.syncFrame !== null) {
        window.cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
      }

      this.surfaceFrames.forEach((frame) => window.cancelAnimationFrame(frame));
      this.surfaceFrames.clear();
    }

    scheduleSync() {
      this.generation += 1;
      const generation = this.generation;
      this.cancelPendingFrames();

      const frame = window.requestAnimationFrame(() => {
        if (this.syncFrame !== frame || !this.isCurrentGeneration(generation)) return;
        this.syncFrame = null;
        this.sync(generation);
      });
      this.syncFrame = frame;
    }

    scheduleSurfaceInitialization(surface, callback, generation) {
      const previousFrame = this.surfaceFrames.get(surface);
      if (previousFrame !== undefined) window.cancelAnimationFrame(previousFrame);

      const frame = window.requestAnimationFrame(() => {
        if (this.surfaceFrames.get(surface) !== frame || !this.isCurrentGeneration(generation)) return;
        this.surfaceFrames.delete(surface);
        callback();
      });
      this.surfaceFrames.set(surface, frame);
    }

    trackSwiper(element, swiper = element?.swiper) {
      if (!swiper) return null;

      this.swipers.add(swiper);
      if (element) this.swiperElements.set(swiper, element);
      return swiper;
    }

    swiperFor(element) {
      const swiper = element?.swiper;
      if (swiper?.destroyed) {
        try {
          delete element.swiper;
        } catch {
          element.swiper = null;
        }
        return null;
      }

      return this.trackSwiper(element, swiper);
    }

    createSwiper(element, options) {
      if (typeof window.Swiper !== 'function' || !element) return null;

      const existing = this.swiperFor(element);
      if (existing) return existing;

      const swiper = new window.Swiper(element, options);
      return this.trackSwiper(element, swiper);
    }

    destroySwiper(element, swiper = element?.swiper) {
      if (!swiper && element) {
        swiper = Array.from(this.swipers).find(
          (candidate) => this.swiperElements.get(candidate) === element
        );
      }
      if (!swiper) return;

      try {
        if (typeof swiper.destroy === 'function') swiper.destroy(true, true);
      } catch {
      }

      this.swipers.delete(swiper);
      this.swiperElements.delete(swiper);
      if (element?.swiper === swiper) {
        try {
          delete element.swiper;
        } catch {
          element.swiper = null;
        }
      }
      if (element) delete element.dataset.ultraMobileSwiperBound;
    }

    versionGalleryElements(scope) {
      if (!scope) return [];

      const elements = [];
      [
        '[data-ebike-version-gallery-main]',
        '[data-ebike-version-gallery-thumbs]',
        '[data-ebike-version-gallery-thumbs-container]'
      ].forEach((selector) => {
        if (scope.matches?.(selector)) elements.push(scope);
        scope.querySelectorAll?.(selector).forEach((element) => elements.push(element));
      });
      return Array.from(new Set(elements));
    }

    destroyVersionGalleryGroup(group) {
      this.versionGalleryElements(group).forEach((element) => {
        this.destroySwiper(element);
      });
    }

    destroyInactiveVersionGallerySwipers(groups, selectedGroups) {
      const selected = new Set(selectedGroups);
      groups.forEach((group) => {
        if (!selected.has(group)) this.destroyVersionGalleryGroup(group);
      });
    }

    destroyAllVersionGallerySwipers() {
      this.versionGalleryElements(this.root).forEach((element) => {
        this.destroySwiper(element);
      });

      this.swipers.forEach((swiper) => {
        const element = this.swiperElements.get(swiper);
        this.destroySwiper(element, swiper);
      });
      this.swipers.clear();
      this.swiperElements.clear();
    }

    syncSurface(surface, color, family, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const panel = this.root.querySelector(`.product-img-panel.${surface}-view`);
      if (!panel) return;

      const slug = colorSlug(color);
      const colorClass = `alt-${slug}`;
      const groups = Array.from(panel.querySelectorAll('[data-ebike-version-gallery-group]'));
      const selectedGroups = groups.filter((group) => (
        normalize(group.dataset.ebikeGalleryColor) === normalize(color) &&
        normalize(group.dataset.ebikeGalleryFamily) === family
      ));
      const useVersionGroup = selectedGroups.length > 0;

      this.destroyInactiveVersionGallerySwipers(groups, selectedGroups);

      groups.forEach((group) => {
        const isSelected = selectedGroups.includes(group);
        group.hidden = !isSelected;
        const showcases = group.matches('.product-showcase')
          ? [group, ...group.querySelectorAll('.product-showcase')]
          : Array.from(group.querySelectorAll('.product-showcase'));

        showcases.forEach((showcase) => {
          removeGalleryColorClasses(showcase);
          showcase.classList.toggle('active', isSelected);
          if (isSelected) showcase.classList.add(colorClass);
        });
      });

      panel.querySelectorAll('[data-ebike-legacy-gallery]').forEach((gallery) => {
        const matchesColor = normalize(gallery.dataset.ebikeGalleryColor) === normalize(color);
        const isSelected = !useVersionGroup && matchesColor;
        gallery.hidden = !isSelected;
        if (gallery.classList.contains('product-showcase')) {
          removeGalleryColorClasses(gallery);
          gallery.classList.toggle('active', isSelected);
          if (isSelected) gallery.classList.add(colorClass);
        }
      });

      if (useVersionGroup) {
        this.scheduleSurfaceInitialization(
          surface,
          () => this.initializeAndReset(surface, selectedGroups, generation),
          generation
        );
      } else {
        this.scheduleSurfaceInitialization(
          surface,
          () => this.resetLegacy(panel, color, generation),
          generation
        );
      }
    }

    initializeAndReset(surface, groups, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const main = groups
        .map((group) => selfOrDescendant(group, '[data-ebike-version-gallery-main]'))
        .find(Boolean);
      const thumbs = groups
        .map((group) => selfOrDescendant(group, '[data-ebike-version-gallery-thumbs]'))
        .find(Boolean);
      if (!main) return;

      if (typeof window.Swiper === 'function') {
        if (surface === 'desktop') {
          const thumbsContainer = thumbs?.querySelector('[data-ebike-version-gallery-thumbs-container]');
          let thumbsSwiper = this.swiperFor(thumbsContainer);
          if (thumbsContainer && !thumbsSwiper) {
            thumbsSwiper = this.createSwiper(thumbsContainer, {
              direction: 'vertical',
              loop: false,
              slidesPerView: Math.max(1, Math.floor((thumbsContainer.clientHeight || 280) / 70)),
              slidesPerGroup: Math.max(1, Math.floor((thumbsContainer.clientHeight || 280) / 70)),
              spaceBetween: 10,
              navigation: {
                nextEl: thumbs.querySelector('.sync2_slider__next'),
                prevEl: thumbs.querySelector('.sync2_slider__prev')
              }
            });
          }
          const mainGroup = main.closest('[data-ebike-version-gallery-group]');
          this.createSwiper(main, {
            direction: 'horizontal',
            loop: false,
            centeredSlides: true,
            allowTouchMove: true,
            navigation: {
              nextEl: mainGroup?.querySelector('.slider__next'),
              prevEl: mainGroup?.querySelector('.slider__prev')
            },
            thumbs: { swiper: thumbsSwiper }
          });
        } else {
          let thumbsSwiper = this.swiperFor(thumbs);
          if (thumbs && !thumbsSwiper) {
            thumbsSwiper = this.createSwiper(thumbs, {
              loop: false,
              speed: 400,
              spaceBetween: 15,
              freeMode: true,
              slidesPerView: 5.5,
              slidesOffsetAfter: 17
            });
          }
          this.createSwiper(main, {
            direction: 'horizontal',
            loop: false,
            speed: 400,
            spaceBetween: 24,
            centeredSlides: true,
            allowTouchMove: true,
            scrollbar: {
              el: main.querySelector('.swiper-scrollbar'),
              draggable: true,
              hide: false,
              snapOnRelease: true
            },
            thumbs: { swiper: thumbsSwiper }
          });
        }
      }

      const thumbsSwiper = surface === 'desktop'
        ? this.swiperFor(thumbs?.querySelector('[data-ebike-version-gallery-thumbs-container]'))
        : this.swiperFor(thumbs);
      const mainSwiper = this.swiperFor(main);
      mainSwiper?.update();
      thumbsSwiper?.update();
      mainSwiper?.slideTo(0, 0);
      thumbsSwiper?.slideTo(0, 0);
      this.updateMobileButtons(main, surface);
    }

    resetLegacy(panel, color, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const main = Array.from(panel.querySelectorAll('[data-ebike-legacy-main]')).find(
        (gallery) => !gallery.hidden && normalize(gallery.dataset.ebikeGalleryColor) === normalize(color)
      );
      const thumbs = Array.from(panel.querySelectorAll('[data-ebike-legacy-thumbs]')).find(
        (gallery) => !gallery.hidden && normalize(gallery.dataset.ebikeGalleryColor) === normalize(color)
      );
      main?.swiper?.update();
      thumbs?.swiper?.update();
      main?.swiper?.slideTo(0, 0);
      thumbs?.swiper?.slideTo(0, 0);
      this.updateMobileButtons(main, panel.classList.contains('mobile-view') ? 'mobile' : 'desktop');
    }

    updateMobileButtons(main, surface) {
      if (!main || surface !== 'mobile') return;
      const hasMultipleSlides = main.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length > 1;
      main.querySelectorAll('[data-ultra-mobile-prev], [data-ultra-mobile-next]').forEach((button) => {
        button.disabled = !hasMultipleSlides;
      });
    }

    sync(generation = this.generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const color = this.selectedColor();
      if (!color) return;
      const family = this.selectedFamily();
      this.syncSurface('desktop', color, family, generation);
      this.syncSurface('mobile', color, family, generation);
    }

    destroy() {
      this.destroyed = true;
      this.generation += 1;
      this.cancelPendingFrames();
      this.form?.removeEventListener('change', this.handleChange);
      window.removeEventListener('load', this.handleLoad);
      this.destroyAllVersionGallerySwipers();
      delete this.root.dataset.ebikeVersionGalleryReady;
    }
  }

  function initialize(scope = document) {
    const roots = [];

    const addRoot = (candidate) => {
      const root = candidate?.matches?.(ROOT_SELECTOR)
        ? candidate
        : candidate?.closest?.(ROOT_SELECTOR);
      if (!root) return;
      if (!roots.includes(root)) roots.push(root);
    };

    if (scope instanceof Element) addRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(addRoot);
    roots.forEach((root) => {
      if (root.dataset.ebikeVersionGalleryReady === 'true') return;
      const instance = new EbikeVersionGallery(root);
      if (root.dataset.ebikeVersionGalleryReady === 'true') instances.set(root, instance);
    });
  }

  function destroyWithin(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains?.(root) || root.contains?.(scope)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
